/**
 * Centralised TanStack Query keys for the Purchases resource (mirrors
 * `ProductQueryKeys`). The draft-invoice mutations write into these caches
 * optimistically and invalidate them for reconciliation — sharing the keys
 * here keeps the widget queries and the mutations from drifting out of step.
 *
 * The read-only lists (incoming / received / import-history) are here too. They
 * used to key on `apiClient.purchases.getX.name` — the function's own name,
 * read at runtime — on the grounds that nothing invalidates them. That was not
 * quite true (the import mutation invalidates import history), and it is unsafe
 * regardless: a minifier renames those methods, and two of them can collide on
 * the same short name, at which point two lists share one cache entry.
 */
export const PurchaseQueryKeys = {
    all: ['purchases'] as const,
    summary: () => [...PurchaseQueryKeys.all, 'summary'] as const,
    draftInvoices: () => [...PurchaseQueryKeys.all, 'draft-invoices'] as const,
    draftInvoiceLists: () =>
        [...PurchaseQueryKeys.draftInvoices(), 'list'] as const,
    draftInvoiceList: (pageSize: number) =>
        [...PurchaseQueryKeys.draftInvoiceLists(), pageSize] as const,
    draftInvoiceDetails: () =>
        [...PurchaseQueryKeys.draftInvoices(), 'detail'] as const,
    draftInvoice: (id: string) =>
        [...PurchaseQueryKeys.draftInvoiceDetails(), id] as const,
    importHistories: () =>
        [...PurchaseQueryKeys.all, 'import-history'] as const,
    importHistory: (pageSize: number) =>
        [...PurchaseQueryKeys.importHistories(), pageSize] as const,
    incomingInvoices: (pageSize: number) =>
        [...PurchaseQueryKeys.all, 'incoming-invoices', pageSize] as const,
    receivedInvoices: (pageSize: number, supplierId: string | null) =>
        [
            ...PurchaseQueryKeys.all,
            'received-invoices',
            pageSize,
            supplierId,
        ] as const,
};
